import { useState, useEffect, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import './index.css'
import {showToast} from "../../utils/messageUtil";
import { useUser } from '../../contexts/UserContext';
import { handleJSAPIAccess } from '../../utils/auth_access_util';

export default function Dispatch() {
    const navigate = useNavigate()
    const location = useLocation()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        const hasName = userInfo.name && userInfo.name.trim() !== ''
        const hasMobile = userInfo.mobile && userInfo.mobile.trim() !== ''
        const hasDepartment = userInfo.department || userInfo.department_name
        return hasUserId && hasName && hasMobile && hasDepartment
    }

    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('派车页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    // 格式化本地时间为ISO格式
    const formatLocalTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
    };

    const getInitialFormData = () => {
        const savedData = localStorage.getItem('dispatchFormData');
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                // 设置开始和结束时间为当前本地时间
                const now = new Date();
                parsedData.expectedStartTime = formatLocalTime(now);
                // 结束时间默认比开始时间晚1小时
                const endTime = new Date(now.getTime() + 60 * 60 * 1000);
                parsedData.expectedEndTime = formatLocalTime(endTime);
                // 每次进入页面时清空用车人和同行人
                parsedData.vehicleUserName = '';
                parsedData.vehicleUserCode = '';
                parsedData.vehicleUserPhone = '';
                parsedData.passengers = '';
                parsedData.passengersCode = '';
                parsedData.expectedLocation = '';
                parsedData.destination = '';
                parsedData.reason = '';
                // 清除缓存的部门信息，确保从最新的用户信息中获取
                parsedData.applicantDept = '';
                return parsedData;
            } catch (error) {
                console.error('解析表单数据失败:', error);
            }
        }
        // 设置开始和结束时间为当前本地时间
        const now = new Date();
        const startTime = formatLocalTime(now);
        // 结束时间默认比开始时间晚1小时
        const endTime = new Date(now.getTime() + 60 * 60 * 1000);
        const endTimeStr = formatLocalTime(endTime);
        return {
            applicantName: '',
            applicantPhone: '',
            applyTime: new Date().toISOString().split('T')[0],
            applicantDept: '',
            employeeId: '',
            applicantOpenId: '',
            plateNumber: '',
            driveType: '1',
            // 开始时间默认为当前本地时间，结束时间默认为当前本地时间+1小时
            expectedStartTime: startTime,
            expectedEndTime: endTimeStr,
            expectedLocation: '',
            destination: '',
            vehicleUserName: '', // 用车人姓名(逗号分隔)
            vehicleUserCode: '', // 用车人openId(逗号分隔)
            passengers: '',
            passengersCode: '',
            reason: ''
        };
    };
    const [formData, setFormData] = useState(getInitialFormData());
    const [submitting, setSubmitting] = useState(false)
    const [availableVehicles, setAvailableVehicles] = useState([])
    const [loadingVehicles, setLoadingVehicles] = useState(false)

    // 当用户信息加载完成后，更新表单数据
    useEffect(() => {
        if (userInfo) {
            // console.log('用户信息已加载1:', userInfo);
            // console.log('部门信息1:', userInfo.department_name);
            // console.log('部门路径1:', userInfo.department_path);
            setFormData(prev => {
                const newDept = userInfo.department_name || prev.applicantDept;
                console.log('准备更新部门信息为1:', newDept);
                return {
                    ...prev,
                    applicantName: userInfo.name || prev.applicantName,
                    applicantPhone: (userInfo.mobile || '').replace(/^\+86/, '') || prev.applicantPhone,
                    employeeId: userInfo.user_id || prev.employeeId,
                    applicantOpenId: userInfo.open_id || userInfo.user_id || prev.applicantOpenId,
                    applicantDept: newDept,
                };
            });
        }
    }, [userInfo]);
    // 获取可预约的车辆
    const fetchAvailableVehicles = useCallback(async () => {
        try {
            const { expectedStartTime, expectedEndTime } = formData

            if (!expectedStartTime || !expectedEndTime) {
                setAvailableVehicles([])
                return
            }

            setLoadingVehicles(true)
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                startTime: expectedStartTime.replace('T', ' '),
                endTime: expectedEndTime.replace('T', ' ')
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                setAvailableVehicles(response.data.data || [])
            } else {
                setAvailableVehicles([])
            }
        } catch (error) {
            console.error('获取可预约车辆失败:', error)
            setAvailableVehicles([])
        } finally {
            setLoadingVehicles(false)
        }
    }, [formData.expectedStartTime, formData.expectedEndTime])


    // 监听formData变化，保存到localStorage
    useEffect(() => {
        localStorage.setItem('dispatchFormData', JSON.stringify(formData));
    }, [formData]);
    // 监听时间变化，获取可预约车辆
    useEffect(() => {
        if (formData.expectedStartTime && formData.expectedEndTime) {
            fetchAvailableVehicles()
        }
    }, [formData.expectedStartTime, formData.expectedEndTime, fetchAvailableVehicles])

    // 从sessionStorage获取位置信息
    useEffect(() => {
        // 延迟执行，确保sessionStorage中的数据已被正确设置
        const timer = setTimeout(() => {
            // 检查是否有新的位置信息格式（包含出发地和目的地）
            const selectedLocations = sessionStorage.getItem('selectedLocations');
            if (selectedLocations) {
                try {
                    const locationsData = JSON.parse(selectedLocations);
                    // console.log('获取到选择的出发地和目的地信息:', locationsData);

                    // 同时更新出发地和目的地
                    setFormData(prev => ({
                        ...prev,
                        expectedLocation: locationsData.from.name,
                        destination: locationsData.to.name
                    }));

                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocations');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                    return;
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocations');
                }
            }

            // 兼容旧的位置信息格式
            const selectedLocation = sessionStorage.getItem('selectedLocation');
            if (selectedLocation) {
                try {
                    const locationData = JSON.parse(selectedLocation);
                    // console.log('获取到选择的位置信息:', locationData);

                    // 根据type赋值给对应的输入框，只更新对应的字段
                    if (locationData.type === 'from') {
                        // 赋值给出发地，保持目的地不变
                        setFormData(prev => ({
                            ...prev,
                            expectedLocation: locationData.location.name
                        }));
                    } else if (locationData.type === 'to') {
                        // 赋值给目的地，保持出发地不变
                        setFormData(prev => ({
                            ...prev,
                            destination: locationData.location.name
                        }));
                    }

                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocation');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocation');
                }
            }

            // 检查是否有单独存储的出发地和目的地信息
            const fromLocation = sessionStorage.getItem('fromLocation');
            const toLocation = sessionStorage.getItem('toLocation');
            if (fromLocation || toLocation) {
                // console.log('获取到单独存储的位置信息:', { fromLocation, toLocation });

                // 更新表单数据
                setFormData(prev => ({
                    ...prev,
                    expectedLocation: fromLocation || prev.expectedLocation,
                    destination: toLocation || prev.destination
                }));

                // 清除sessionStorage中的数据，避免重复赋值
                sessionStorage.removeItem('fromLocation');
                sessionStorage.removeItem('toLocation');
                console.log('单独存储的位置信息赋值完成，已清除sessionStorage中的数据');
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [location])

    const handleChange = (e) => {
        const { name, value } = e.target
        // 确保时间字段包含秒数
        let processedValue = value
        if (name === 'expectedStartTime' || name === 'expectedEndTime') {
            if (value.length === 16) {
                // 将value格式化为YYYY-MM-DDTHH:mm:ss
                processedValue = value + ':00'
            }
        }

        // 更新表单数据
        setFormData(prev => {
            let newFormData = {
                ...prev,
                [name]: processedValue
            }
            
            // 当修改开始时间时，结束时间自动+60分钟
            if (name === 'expectedStartTime') {
                const startTime = new Date(newFormData.expectedStartTime)
                const endTime = new Date(newFormData.expectedEndTime)
                // 计算时间差（分钟）
                const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
                
                // 如果结束时间早于开始时间或时长小于60分钟，自动调整
                if (endTime <= startTime || durationMinutes < 60) {
                    // 自动调整结束时间为开始时间之后1小时
                    const adjustedEndTime = new Date(startTime.getTime() + 60 * 60 * 1000)
                    newFormData.expectedEndTime = formatLocalTime(adjustedEndTime)
                    showToast('结束时间已自动调整为开始时间之后1小时', 'info')
                }
            } else if (name === 'expectedEndTime') {
                // 当修改结束时间时，校验时长不能低于60分钟
                const startTime = new Date(newFormData.expectedStartTime)
                const endTime = new Date(newFormData.expectedEndTime)
                
                // 计算时间差（分钟）
                const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
                
                // 如果结束时间早于开始时间或时长小于60分钟，不更新并提示
                if (endTime <= startTime || durationMinutes < 60) {
                    // 恢复原来的值
                    newFormData.expectedEndTime = prev.expectedEndTime
                    showToast('预约时长不能低于60分钟', 'error')
                }
            }
            
            return newFormData
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        // 防止重复提交
        if (submitting) {
            return
        }

        // 检查必填项
        if (!formData.vehicleUserName || !formData.vehicleUserCode || !formData.reason || !formData.expectedStartTime || !formData.expectedEndTime) {
            showToast('请填写必填项', 'error')
            return
        }
        
        // 检查申请人部门是否为空
        if (!formData.applicantDept || formData.applicantDept.trim() === '') {
            showToast('申请人部门信息获取失败，请刷新页面重试', 'error')
            return
        }
        
        // 检查结束时间是否大于开始时间
        const startTime = new Date(formData.expectedStartTime)
        const endTime = new Date(formData.expectedEndTime)
        if (endTime <= startTime) {
            showToast('结束时间必须大于开始时间', 'error')
            return
        }
        
        // 校验时长不能低于60分钟
        const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
        if (durationMinutes < 60) {
            showToast('预约时长不能低于60分钟', 'error')
            return
        }

        try {
            setSubmitting(true)
            // 构建请求参数，与预约页面保持一致
            const requestData = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                plateNumber: formData.plateNumber,
                applicantId: formData.employeeId,
                applicantName: formData.applicantName,
                applicantPhone: formData.applicantPhone,
                applicantDept: formData.applicantDept,
                applyTime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                reason: formData.reason,
                expectedStartTime: formData.expectedStartTime.replace('T', ' '),
                expectedEndTime: formData.expectedEndTime.replace('T', ' '),
                expectedLocation: formData.expectedLocation,
                destination: formData.destination,
                driveType: 1, // 默认自驾
                isPersonal: 1, // 派车默认为代人申请
                vehicleUserName: formData.vehicleUserName,
                vehicleUserCode: formData.vehicleUserCode,
                passengers: formData.passengers,
                passengersCode: formData.passengersCode
            }

            console.log('提交派车信息:', requestData)

            // 调用与预约页面相同的后端API
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/dispatch`, requestData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            console.log('API响应:', response.data)

            // 处理响应
            if (response.data && response.data.code === 200) {
                showToast('派车成功！', 'success')
                // 清除localStorage中的表单数据
                localStorage.removeItem('dispatchFormData');
                navigate('/')
            } else {
                showToast(`派车失败: ${response.data.message || '未知错误'}`, 'error')
            }
        } catch (error) {
            console.error('提交派车失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = () => {
        navigate('/')
    }

    // 跳转到地图选择页面选择出发地
    const handleChooseFromLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/dispatch');
        console.log('存储当前页面路径到sessionStorage:', '/dispatch');
        navigate('/map-select/from')
    }

    // 跳转到地图选择页面选择目的地
    const handleChooseToLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/dispatch');
        console.log('存储当前页面路径到sessionStorage:', '/dispatch');
        navigate('/map-select/to')
    }

    // 处理联系人选择结果
    const handleContactSelectResult = (res) => {
        console.log('处理联系人选择结果:', res);

        // 处理选择的用户
        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用户:', selectedUsers);

            // 过滤掉已选择的用车人
            const vehicleUserIds = formData.vehicleUserCode ? formData.vehicleUserCode.split(',').filter(id => id.trim()) : [];
            let filteredByDriver = 0;

            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除已选择的用车人
                if (vehicleUserIds.includes(userOpenId)) {
                    filteredByDriver++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByDriver > 0) {
                    showToast('不能选择已选择的用车人作为同行人，请重新选择', 'error');
                } else {
                    showToast('请重新选择同行人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                showToast('已排除已选择的用车人，不能选择作为同行人', 'error');
            }

            // 提取姓名和openId,分别用逗号分隔
            const names = filteredUsers.map(user => user.name).filter(name => name);
            const openIds = filteredUsers.map(user => user.openId).filter(openId => openId);

            console.log('提取的姓名:', names);
            console.log('提取的openId:', openIds);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                passengers: names.join(','), // 逗号分隔的姓名
                passengersCode: openIds.join(',') // 逗号分隔的openId
            }));

            showToast(`已选择 ${names.length} 位联系人`, 'success');
        } else {
            console.warn('未选择联系人');
            showToast('未选择联系人', 'error');
        }
    };

    // 打开飞书联系人选择器
    const openLarkContactPicker = async () => {
        console.log('openLarkContactPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 检测是否在开发者工具中
        const isDevTools = window.navigator.userAgent.toLowerCase().includes('devtools') ||
            window.location.href.includes('devtools');
        if (isDevTools) {
            console.log('检测到开发者工具环境，使用模拟数据');
            // 在开发者工具中使用模拟数据
            const mockData = {
                data: [
                    { openId: 'ou_dev_test_1', name: '测试同行人1', mobile: '+8613800138001' },
                    { openId: 'ou_dev_test_2', name: '测试同行人2', mobile: '+8613800138002' }
                ],
                department_data: []
            };
            handleContactSelectResult(mockData);
            return;
        }

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact');
        tt.chooseContact({
            multi: true,
            ignore: false,
            maxNum: 10,
            limitTips: "最多只能选择10个同行人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleContactSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择同行人:', res);
            }
        });
    };

    // 打开用车人选择器
    const openDriverPicker = async () => {
        console.log('openDriverPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 检测是否在开发者工具中
        const isDevTools = window.navigator.userAgent.toLowerCase().includes('devtools') ||
            window.location.href.includes('devtools');
        if (isDevTools) {
            console.log('检测到开发者工具环境，使用模拟数据');
            const mockData = {
                data: [
                    { openId: 'ou_dev_test_1', name: '测试司机1', mobile: '+8613800138001' }
                ],
                department_data: []
            };
            handleDriverSelectResult(mockData);
            return;
        }

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact 选择用车人');
        tt.chooseContact({
            multi: false, // 用车人只能选一个
            ignore: false,
            maxNum: 1,
            limitTips: "只能选择1个用车人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleDriverSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择用车人:', res);
            }
        });
    };

    // 处理用车人选择结果
    const handleDriverSelectResult = (res) => {
        console.log('处理用车人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用车人:', selectedUsers);

            // 过滤掉已选择的同行人
            const passengerIds = formData.passengersCode ? formData.passengersCode.split(',').filter(id => id.trim()) : [];
            let filteredByPassenger = 0;
            
            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除已选择的同行人
                if (passengerIds.includes(userOpenId)) {
                    filteredByPassenger++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByPassenger > 0) {
                    showToast('不能选择已选择的同行人作为用车人，请重新选择', 'error');
                } else {
                    showToast('请重新选择用车人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                showToast('已排除已选择的同行人，不能选择作为用车人', 'info');
            }

            // 提取姓名、openId和电话
            const user = filteredUsers[0];
            const name = user.name || '';
            const userOpenId = user.open_id || user.openId || user.user_id || '';
            const phone = (user.mobile || '').replace(/^\+86/, '') || '';

            console.log('提取的姓名:', name);
            console.log('提取的openId:', userOpenId);
            console.log('提取的电话:', phone);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                vehicleUserName: name,
                vehicleUserCode: userOpenId,
                vehicleUserPhone: phone
            }));

            showToast(`已选择用车人: ${name}`, 'success');
        } else {
            console.warn('未选择用车人');
            showToast('未选择用车人', 'info');
        }
    };

    return (
        <div className="dispatch-page">
            <h2>新增派车</h2>
            {userLoading && (
            <div className="loading-indicator">
                <p>正在获取用户信息...</p>
            </div>
        )}
            <form onSubmit={handleSubmit} className="dispatch-form">
                <div className="form-group">
                    <label>用车人:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="vehicleUserName"
                            value={formData.vehicleUserName}
                            onChange={handleChange}
                            required
                            readOnly
                            placeholder="可通过选择组织内联系人添加"
                        />
                        <button type="button" className="contact-select-btn" onClick={openDriverPicker} title="选择用车人">
                            👤
                        </button>
                    </div>
                </div>
{/*                <div className="form-group">
                    <label>电话号码:</label>
                    <input
                        type="tel"
                        name="vehicleUserPhone"
                        value={formData.vehicleUserPhone}
                        onChange={handleChange}
                        readOnly
                        placeholder="添加组织内联系人自动获取"
                    />
                </div>*/}
                <div className="form-group">
                    <label>开始时间:<span className="required">*</span></label>
                    <input
                        type="datetime-local"
                        name="expectedStartTime"
                        value={formData.expectedStartTime.slice(0, 16)}
                        onChange={handleChange}
                        required
                        step="60"
                    />
                </div>
                <div className="form-group">
                    <label>结束时间:<span className="required">*</span></label>
                    <input
                        type="datetime-local"
                        name="expectedEndTime"
                        value={formData.expectedEndTime.slice(0, 16)}
                        onChange={handleChange}
                        required
                        step="60"
                    />
                </div>
                <div className="form-group">
                    <label>车牌号:<span className="required">*</span></label>
                    <select
                        name="plateNumber"
                        value={formData.plateNumber}
                        onChange={handleChange}
                        required
                    >
                        <option value="">选择时间后显示</option>
                        {availableVehicles.map((vehicle, index) => (
                            <option key={index} value={vehicle.plateNumber}>
                                {vehicle.plateNumber} {vehicle.modelName}
                            </option>
                        ))}
                    </select>
                    {loadingVehicles && <div className="loading-text">正在加载车辆...</div>}
                </div>
                <div className="form-group">
                    <label>出发地:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="expectedLocation"
                            value={formData.expectedLocation}
                            onChange={handleChange}
                            required
                            placeholder="请输入出发地"
                        />
                        <button type="button" className="contact-select-btn" onClick={handleChooseFromLocation} title="选择出发地">
                            📍
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label>目的地:<span className="required">*</span></label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="destination"
                            value={formData.destination}
                            onChange={handleChange}
                            required
                            placeholder="请输入目的地"
                        />
                        <button type="button" className="contact-select-btn" onClick={handleChooseToLocation} title="选择目的地">
                            📍
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label>用车原因:<span className="required">*</span></label>
                    <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        required
                        placeholder="请输入用车原因"
                    ></textarea>
                </div>
                <div className="form-group">
                    <label>同行人:</label>
                    <div className="contact-input-wrapper">
                        <input
                            type="text"
                            name="passengers"
                            value={formData.passengers}
                            onChange={handleChange}
                            readOnly
                            placeholder="可通过选择组织内联系人添加"
                        />
                        <button type="button" className="contact-select-btn" onClick={openLarkContactPicker} title="选择同行人">
                            👥
                        </button>
                    </div>
                </div>
                <div className="form-actions">
                    <button type="submit" className="submit-btn" disabled={submitting}>
                        {submitting ? '提交中...' : '确定'}
                    </button>
                    <button type="button" className="cancel-btn" onClick={handleCancel}>
                        取消
                    </button>
                </div>
            </form>
        </div>
    )
}